import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  appointmentsApi,
  bookingApi,
  type AppointmentKind,
} from './appointments'
import { queryKeys } from './query-keys'

// Booking mutations invalidate appointments AND estimates — booking an
// estimate flips it to Scheduled, so the board/card/inbox must follow.

export function useAppointments(
  teamId: number | string | null | undefined,
  from: string,
  to: string,
) {
  return useQuery({
    queryKey: queryKeys.appointments.byTeam(teamId ?? 'none', from, to),
    queryFn: () => appointmentsApi.listByTeam(Number(teamId), from, to),
    enabled: teamId != null,
    refetchInterval: 30_000,
  })
}

export function useSlots(
  teamId: number | string | null | undefined,
  from: string,
  to: string,
) {
  return useQuery({
    queryKey: queryKeys.appointments.slots(teamId ?? 'none', from, to),
    queryFn: () => appointmentsApi.slots(Number(teamId), from, to),
    enabled: teamId != null,
    refetchInterval: 30_000,
  })
}

function useInvalidateBookingWorld() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.appointments.all })
    qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
    qc.invalidateQueries({ queryKey: queryKeys.customerActivity.all })
    qc.invalidateQueries({ queryKey: queryKeys.campaigns.all })
    // A booking closes the estimate's open tasks server-side; the list has
    // to advance without waiting for its own poll.
    qc.invalidateQueries({ queryKey: queryKeys.tasks.all })
  }
}

export function useCreateAppointment() {
  const invalidate = useInvalidateBookingWorld()
  return useMutation({
    mutationFn: (data: Parameters<typeof appointmentsApi.create>[0]) =>
      appointmentsApi.create(data),
    onSuccess: invalidate,
  })
}

export function useRescheduleAppointment() {
  const invalidate = useInvalidateBookingWorld()
  return useMutation({
    mutationFn: ({ id, startsAt }: { id: number; startsAt: string }) =>
      appointmentsApi.reschedule(id, startsAt),
    onSuccess: invalidate,
  })
}

export function useCancelAppointment() {
  const invalidate = useInvalidateBookingWorld()
  return useMutation({
    mutationFn: (id: number) => appointmentsApi.cancel(id),
    onSuccess: invalidate,
  })
}

/** Record attended / no-show after the slot passes. A no-show rolls the
 *  estimate back and re-opens the customer's booking link. */
export function useSetAppointmentOutcome() {
  const invalidate = useInvalidateBookingWorld()
  return useMutation({
    mutationFn: ({ id, outcome }: { id: number; outcome: 'attended' | 'no_show' }) =>
      appointmentsApi.setOutcome(id, outcome),
    onSuccess: invalidate,
  })
}

/* ── The customer's side (public /book/:token page) ──────── */

export function useBookingInfo(token: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.appointments.booking(token ?? 'none'),
    queryFn: () => bookingApi.info(String(token)),
    enabled: !!token,
    retry: false,
  })
}

export function useBookingSlots(
  token: string | null | undefined,
  from: string,
  to: string,
) {
  return useQuery({
    queryKey: queryKeys.appointments.bookingSlots(token ?? 'none', from, to),
    queryFn: () => bookingApi.slots(String(token), from, to),
    enabled: !!token,
  })
}

export function useConfirmBooking(token: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ startsAt, kind }: { startsAt: string; kind: AppointmentKind }) =>
      bookingApi.confirm(String(token), startsAt, kind),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.appointments.all })
    },
  })
}

export function useCancelBooking(token: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => bookingApi.cancel(String(token)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.appointments.all })
    },
  })
}
